import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';

// The 'api.js' file should contain all your fetch functions
import {
  fetchConnections,
  fetchOqlousTools,
  fetchGlobalConnections,
} from '../components/McpConnections/api';
// Assuming DataState provides the user's auth token
import { DataState } from './DataProvider';

const McpContext = createContext();

export const McpProvider = ({ children }) => {
  const [connections, setConnections] = useState([]);
  const [oqlousTools, setOqlousTools] = useState([]);
  const [globalConnections, setGlobalConnections] = useState([]); // NEW: State for global connections
  const [isLoading, setIsLoading] = useState(true); // Set initial loading to true
  const [error, setError] = useState(null);
  const [checkResult, setCheckResult] = useState(null);

  // NEW: State to trigger a refresh. Incrementing it will cause the useEffect to re-run.
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const { userGoogleId: accessToken } = DataState();

  // COMBINED loading function for better performance and state management
  const loadAllMcpData = useCallback(async () => {
    if (!accessToken) {
      // Clear all data on logout
      setConnections([]);
      setOqlousTools([]);
      setGlobalConnections([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Use Promise.all to fetch all data in parallel
      const [connectionsData, toolsData, globalData] = await Promise.all([
        fetchConnections(accessToken),
        fetchOqlousTools(accessToken),
        fetchGlobalConnections(accessToken),
      ]);

      setConnections(connectionsData || []);
      setOqlousTools(toolsData || []);
      setGlobalConnections(globalData || []);
    } catch (err) {
      setError('Failed to load connection data.');
    } finally {
      // Set loading to false only once after all fetches are complete
      setIsLoading(false);
    }
  }, [accessToken]); // This function only changes when the access token changes

  // BEST PRACTICE FIX: The loadConnections function depends on 'accessToken'.
  // It MUST be included in the dependency array of useCallback.
  // Otherwise, it will forever use the initial value of accessToken (a "stale closure").
  const loadConnections = useCallback(async () => {
    // Don't try to fetch if the access token isn't available yet.
    if (!accessToken) {
      setConnections([]); // Ensure data is cleared on logout
      setIsLoading(false);
      return;
    }

    try {
      setError(null);
      setIsLoading(true);
      const response = await fetchConnections(accessToken);
      setConnections(response || []); // Handle cases where the API might not return an array
    } catch (err) {
      setError('Failed to load connections.');
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]); // FIX: Added accessToken to the dependency array.

  useEffect(() => {
    loadAllMcpData();
  }, [loadAllMcpData, refreshTrigger]); // UPDATE: Added refreshTrigger as a dependency.

  // NEW: A stable function to be called by child components to trigger a refresh.
  // useCallback with an empty dependency array ensures this function itself never changes.
  const refreshConnections = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  return (
    <McpContext.Provider
      value={{
        connections,
        setConnections,
        isLoading,
        setIsLoading,
        error,
        setError,
        loadConnections,
        refreshConnections,
        oqlousTools,
        setOqlousTools,
        checkResult,
        setCheckResult,
        globalConnections, // NEW: Expose global connections in the context
      }}
    >
      {children}
    </McpContext.Provider>
  );
};

// Custom hook for easy consumption.
export const useMCP = () => useContext(McpContext);
